import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// functions
import { ServiceStatusParams } from '../../utils/events';
import { Site } from '../../utils/types';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface SearchComputeState {
  sDescription: string;
  sGPU: boolean;
  sLargeScratch: boolean;
  sHighMemory: boolean;
  sFastScratch: boolean;
  sNodeValue: string;
  sSiteValue: string;
  sHardwareType: string;
  sServiceType: string;
  sAvailabilityType: string;
  sMiddlewareVersion: string;
  sTableNode: string;
  sTableSite: string;
  sTableGPU: boolean;
  sTableLargeScratch: boolean;
  sTableHighMemory: boolean;
  sTableFastScratch: boolean;
  sTableDescription: string;
  sTableHardwareType: string;
  sTableServiceType: string;
  sTableAvailabilityType: string;
  sTableMiddlewareVersion: string;

  // Search Compute Table state variables
  sLoadingComponent: boolean;
  sUpdateServiceStatus: ServiceStatusParams[];
  // the flattened list of services, including information such as node, site, and site_uuid.
  sServicesList: any[];
}

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: SearchComputeState = {
  sDescription: '',
  sGPU: false,
  sLargeScratch: false,
  sHighMemory: false,
  sFastScratch: false,
  sNodeValue: '',
  sSiteValue: '',
  sHardwareType: '',
  sServiceType: '',
  sAvailabilityType: '',
  sMiddlewareVersion: '',
  sTableNode: '',
  sTableSite: '',
  sTableGPU: false,
  sTableLargeScratch: false,
  sTableHighMemory: false,
  sTableFastScratch: false,
  sTableDescription: '',
  sTableHardwareType: '',
  sTableServiceType: '',
  sTableAvailabilityType: '',
  sTableMiddlewareVersion: '',

  // Search Compute Table state variables
  sLoadingComponent: true,
  sUpdateServiceStatus: [],
  sServicesList: [],
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const SearchComputeSlice = createSlice({
  name: 'searchCompute',
  initialState: initialState,
  reducers: {
    setDescription: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sDescription = action.payload;
    },
    setGPU: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sGPU = action.payload;
    },
    setLargeScratch: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sLargeScratch = action.payload;
    },
    setHighMemory: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sHighMemory = action.payload;
    },
    setFastScratch: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sFastScratch = action.payload;
    },
    setNodeValue: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sNodeValue = action.payload;
    },
    setSiteValue: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sSiteValue = action.payload;
    },
    setHardwareType: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sHardwareType = action.payload;
    },
    setServiceType: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sServiceType = action.payload;
    },
    setAvailabilityType: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sAvailabilityType = action.payload;
    },
    setMiddlewareVersion: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sMiddlewareVersion = action.payload;
    },
    setTableNode: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sTableNode = action.payload;
    },
    setTableSite: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sTableSite = action.payload;
    },
    setTableGPU: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sTableGPU = action.payload;
    },
    setTableLargeScratch: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sTableLargeScratch = action.payload;
    },
    setTableHighMemory: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sTableHighMemory = action.payload;
    },
    setTableFastScratch: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sTableFastScratch = action.payload;
    },
    setTableDescription: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sTableDescription = action.payload;
    },
    setTableHardwareType: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sTableHardwareType = action.payload;
    },
    setTableServiceType: (state: SearchComputeState, action: PayloadAction<string>) => {
      state.sTableServiceType = action.payload;
    },
    setTableAvailabilityType: (
      state: SearchComputeState,
      action: PayloadAction<string>,
    ) => {
      state.sTableAvailabilityType = action.payload;
    },
    setTableMiddlewareVersion: (
      state: SearchComputeState,
      action: PayloadAction<string>,
    ) => {
      state.sTableMiddlewareVersion = action.payload;
    },
    setLoadingComponent: (state: SearchComputeState, action: PayloadAction<boolean>) => {
      state.sLoadingComponent = action.payload;
    },
    setUpdateServiceStatus: (
      state: SearchComputeState,
      action: PayloadAction<ServiceStatusParams[]>,
    ) => {
      state.sUpdateServiceStatus = action.payload;
    },
    setServicesList: (state: SearchComputeState, action: PayloadAction<any[]>) => {
      state.sServicesList = action.payload;
    },

    //
  },
});

// expose the interface.
export const {
  setDescription,
  setGPU,
  setLargeScratch,
  setHighMemory,
  setFastScratch,
  setNodeValue,
  setSiteValue,
  setHardwareType,
  setServiceType,
  setAvailabilityType,
  setMiddlewareVersion,
  setTableNode,
  setTableSite,
  setTableGPU,
  setTableLargeScratch,
  setTableHighMemory,
  setTableFastScratch,
  setTableDescription,
  setTableHardwareType,
  setTableServiceType,
  setTableAvailabilityType,
  setTableMiddlewareVersion,
  setLoadingComponent,
  setUpdateServiceStatus,
  setServicesList,
} = SearchComputeSlice.actions;
export default SearchComputeSlice.reducer;
